import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Card, IconButton, Typography } from '@mui/material';
import ImageLightbox from 'components/ImageLightbox/ImageLightbox';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import imgFunction4 from 'assets/guide/functions4.png';

const IMAGE_SLIDE_INTERVAL_MS = 3500;
const SLIDE_TRANSITION_MS = 550;

const mediaHeightSx = {
  xs: 'min(42vh, 280px)',
  sm: 'min(46vh, 360px)',
  md: 'min(48vh, 420px)'
};

const slides = [
  { video: '/videos/vid-1.MOV', titleKey: 'landing.process.step1.title', descKey: 'landing.process.step1.desc' },
  { video: '/videos/vid-2.MP4', titleKey: 'landing.process.step2.title', descKey: 'landing.process.step2.desc' },
  { video: '/videos/vid-3.MP4', titleKey: 'landing.process.step3.title', descKey: 'landing.process.step3.desc' },
  { image: imgFunction4, titleKey: 'landing.process.step4.title', descKey: 'landing.process.step4.desc' }
];

export default function LandingProcessCarousel() {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [holding, setHolding] = useState(false);
  const [lightbox, setLightbox] = useState({
    open: false,
    url: '',
    name: '',
    index: 0,
    mediaType: 'image'
  });
  const holdingRef = useRef(false);
  const indexRef = useRef(0);
  const lightboxOpenRef = useRef(false);
  const videoRefs = useRef([]);

  const activeSlide = slides[index];
  const isVideoSlide = Boolean(activeSlide?.video);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    lightboxOpenRef.current = lightbox.open;
  }, [lightbox.open]);

  const goTo = useCallback((nextIndex) => {
    const total = slides.length;
    setIndex(((nextIndex % total) + total) % total);
  }, []);

  const goNext = useCallback(() => {
    setIndex((current) => (current + 1) % slides.length);
  }, []);

  const goPrev = useCallback(() => {
    setIndex((current) => (current - 1 + slides.length) % slides.length);
  }, []);

  // Pause/reset inactive videos; play active video when allowed.
  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i !== index) {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (e) {
          // ignore seek errors while metadata loads
        }
      }
    });

    const activeVideo = videoRefs.current[index];
    if (!activeVideo || !slides[index]?.video) {
      return undefined;
    }

    if (holding || lightbox.open) {
      activeVideo.pause();
      return undefined;
    }

    const playPromise = activeVideo.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }

    return undefined;
  }, [index, holding, lightbox.open]);

  // Image slide auto-advance only (videos advance on ended).
  useEffect(() => {
    if (holding || lightbox.open || isVideoSlide) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      goNext();
    }, IMAGE_SLIDE_INTERVAL_MS);

    return () => window.clearTimeout(timeout);
  }, [holding, lightbox.open, isVideoSlide, index, goNext]);

  useEffect(() => {
    const releaseHold = () => {
      if (!holdingRef.current) return;
      holdingRef.current = false;
      setHolding(false);
    };

    window.addEventListener('mouseup', releaseHold);
    window.addEventListener('touchend', releaseHold);
    window.addEventListener('touchcancel', releaseHold);
    return () => {
      window.removeEventListener('mouseup', releaseHold);
      window.removeEventListener('touchend', releaseHold);
      window.removeEventListener('touchcancel', releaseHold);
    };
  }, []);

  const handleHoldStart = (event) => {
    if (event.target.closest?.('button')) return;
    holdingRef.current = true;
    setHolding(true);
  };

  const openLightboxAt = useCallback(
    (slideIndex) => {
      const slide = slides[slideIndex];
      if (!slide) return;
      const isVideo = Boolean(slide.video);
      setIndex(slideIndex);
      setLightbox({
        open: true,
        url: isVideo ? slide.video : slide.image,
        name: t(slide.titleKey),
        index: slideIndex,
        mediaType: isVideo ? 'video' : 'image'
      });
    },
    [t]
  );

  const handleLightboxClose = () => {
    setLightbox({ open: false, url: '', name: '', index: 0, mediaType: 'image' });
  };

  const handleLightboxPrev = () => {
    const nextIdx = (lightbox.index - 1 + slides.length) % slides.length;
    openLightboxAt(nextIdx);
  };

  const handleLightboxNext = () => {
    const nextIdx = (lightbox.index + 1) % slides.length;
    openLightboxAt(nextIdx);
  };

  const handleDownloadImage = () => {
    if (!lightbox.url) return;
    const a = document.createElement('a');
    a.href = lightbox.url;
    a.download =
      lightbox.mediaType === 'video'
        ? `${lightbox.name || 'guide'}.mp4`
        : `${lightbox.name || 'guide'}.jpg`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleVideoEnded = (slideIndex) => {
    // Advance only when this video is the active one and lightbox is closed.
    if (slideIndex !== indexRef.current) return;
    if (lightboxOpenRef.current) return;
    goNext();
  };

  const handleVideoError = (slideIndex) => {
    if (slideIndex !== indexRef.current) return;
    if (lightboxOpenRef.current) return;
    window.setTimeout(() => {
      if (slideIndex === indexRef.current) goNext();
    }, IMAGE_SLIDE_INTERVAL_MS);
  };

  return (
    <Box sx={{ mb: { xs: 4, md: 5 } }}>
      <Typography
        variant="h5"
        textAlign="center"
        fontWeight={700}
        sx={{ mb: { xs: 1.5, md: 2 }, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
        {t('landing.process.title')}
      </Typography>

      <Card
        sx={{
          overflow: 'hidden',
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(32, 101, 209, 0.1)'
        }}>
        <Box
          onMouseDown={handleHoldStart}
          onTouchStart={handleHoldStart}
          sx={{ position: 'relative', width: '100%', userSelect: holding ? 'none' : undefined }}>
          <Box sx={{ overflow: 'hidden', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                transform: `translateX(-${index * 100}%)`,
                transition: `transform ${SLIDE_TRANSITION_MS}ms ease-in-out`
              }}>
              {slides.map((slide, slideIndex) => (
                <Box
                  key={slide.titleKey}
                  sx={{
                    flex: '0 0 100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                  {slide.video ? (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        openLightboxAt(slideIndex);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openLightboxAt(slideIndex);
                        }
                      }}
                      sx={{
                        cursor: 'zoom-in',
                        lineHeight: 0,
                        bgcolor: 'grey.100'
                      }}>
                      <Box
                        component="video"
                        ref={(el) => {
                          videoRefs.current[slideIndex] = el;
                        }}
                        src={slide.video}
                        muted
                        playsInline
                        preload="auto"
                        onEnded={() => handleVideoEnded(slideIndex)}
                        onError={() => handleVideoError(slideIndex)}
                        sx={{
                          display: 'block',
                          width: '100%',
                          height: mediaHeightSx,
                          objectFit: 'cover',
                          pointerEvents: 'none'
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        openLightboxAt(slideIndex);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openLightboxAt(slideIndex);
                        }
                      }}
                      sx={{
                        cursor: 'zoom-in',
                        lineHeight: 0,
                        bgcolor: 'grey.100'
                      }}>
                      <Box
                        component="img"
                        src={slide.image}
                        alt={t(slide.titleKey)}
                        loading="eager"
                        sx={{
                          display: 'block',
                          width: '100%',
                          height: mediaHeightSx,
                          objectFit: 'cover',
                          pointerEvents: 'none'
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          <IconButton
            aria-label="previous-slide"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              left: { xs: 4, md: 8 },
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': { bgcolor: 'background.paper' },
              zIndex: 2
            }}>
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            aria-label="next-slide"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              right: { xs: 4, md: 8 },
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': { bgcolor: 'background.paper' },
              zIndex: 2
            }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Box
          key={`caption-${index}`}
          sx={{
            px: { xs: 2, md: 2.5 },
            py: { xs: 1.5, md: 2 },
            '@keyframes fadeCaption': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'translateY(0)' }
            },
            animation: 'fadeCaption 280ms ease'
          }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 0.5 }}>
            {t(activeSlide.titleKey)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(activeSlide.descKey)}
          </Typography>
        </Box>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.25, mt: 1.5 }}>
        {slides.map((slide, slideIndex) => (
          <Box
            key={slide.titleKey}
            onClick={() => goTo(slideIndex)}
            sx={{
              width: index === slideIndex ? 18 : 8,
              height: 8,
              borderRadius: 99,
              bgcolor: index === slideIndex ? 'primary.main' : 'divider',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          />
        ))}
      </Box>

      <ImageLightbox
        open={lightbox.open}
        url={lightbox.url}
        handleClose={handleLightboxClose}
        download={handleDownloadImage}
        imageName={lightbox.name || t(activeSlide.titleKey)}
        mediaType={lightbox.mediaType}
        onPrev={handleLightboxPrev}
        onNext={handleLightboxNext}
      />
    </Box>
  );
}
